// تسجيل عامل الخدمة — به يصير البرنامج **تطبيقًا** يُثبَّت على الموبايل بأيقونته وملء شاشته.
//
// **في التطوير لا يُسجَّل**: عاملُ خدمةٍ يخزّن هيكل البرنامج أثناء التعديل يُري المطوّر نسخةً
// قديمة ويُضيّع ساعات في مطاردة خطأٍ أُصلح. وإن كان مسجَّلًا من قبلُ على المتصفّح نفسه أُزيل.
//
// و**لا يُسجَّل إلا على أصلٍ آمن** (https أو localhost) — وهذا شرط المتصفّح لا اختيارنا.
export function registerPwa(): void {
  if (!('serviceWorker' in navigator)) return;

  if (import.meta.env.DEV) {
    void navigator.serviceWorker.getRegistrations().then(rs => rs.forEach(r => void r.unregister()));
    return;
  }

  // ضغطةٌ على إشعار الجهاز والبرنامج مفتوح: عامل الخدمة يطلب الانتقال إلى الشاشة (0145)
  navigator.serviceWorker.addEventListener('message', e => {
    const d = e.data as { type?: string; url?: string } | null;
    if (d?.type === 'open-url' && d.url) { try { window.location.hash = new URL(d.url, window.location.origin).hash; } catch { /* عنوانٌ تالف */ } }
  });

  window.addEventListener('load', () => {
    // `?v=<معرّف البناء>`: عنوانٌ جديد كل بناء فيُكتشف الإصدارُ الجديد (تدقيق 23/09/2026)
    void navigator.serviceWorker.register(`/sw.js?v=${__BUILD_ID__}`).then(reg => {
      // نسخةٌ جديدة وصلت والصفحة مفتوحة: تُطبَّق فورًا لا بعد إغلاق التطبيق
      reg.addEventListener('updatefound', () => {
        const w = reg.installing;
        w?.addEventListener('statechange', () => {
          if (w.state === 'installed' && navigator.serviceWorker.controller) w.postMessage('skip-waiting');
        });
      });
    }).catch(() => { /* المتصفّح منع التسجيل — البرنامج يعمل بلا تطبيق */ });
  });

  // بعد تبديل العامل: إعادة تحميلٍ واحدة لا حلقة — **ولا في أول زيارة** (مراجعة 23/09/2026): أوّلُ
  // عاملٍ يتسلّم الصفحة يُطلق الحدث نفسه، فكانت صفحةُ «تعيين كلمة المرور» تُعاد وسط الكتابة
  const hadController = !!navigator.serviceWorker.controller;
  let reloaded = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloaded || !hadController) return;
    reloaded = true;
    window.location.reload();
  });

  watchForNewBuild();
}

/** بعد كم من الغياب يُسأل الخادم عن نسخةٍ أحدث، وكل كم وهو ظاهر. */
const RECHECK_AFTER_HIDDEN_MS = 5 * 60_000;
const RECHECK_WHILE_VISIBLE_MS = 30 * 60_000;

/** سكربت البناء الذي تعمل به الصفحة الآن — بصمتُه في اسمه، فتغيُّره = بناءٌ جديد نُشر. */
const entryScript = (html: string): string | null => html.match(/\/assets\/index-[\w-]+\.js/)?.[0] ?? null;

/**
 * هل نُشرت نسخةٌ أحدث من التي تعمل بها الصفحة؟ تُقارَن بصمةُ سكربت البناء في الصفحة الحالية
 * بما في `index.html` الخادم الآن. وأيُّ شكٍّ (لا شبكة، ردٌّ غير HTML، صفحةٌ بلا بصمة) = لا.
 */
export async function isNewBuildOut(doc: Document = document, fetchImpl: typeof fetch = fetch): Promise<boolean> {
  const current = entryScript(doc.querySelector('script[type="module"][src*="/assets/"]')?.getAttribute('src') ?? '');
  if (!current) return false;
  try {
    // لا يمرّ بعامل الخدمة كصفحة (ليس تنقّلًا)، وno-store فلا تجيب ذاكرةُ المتصفّح
    const r = await fetchImpl('/', { cache: 'no-store', credentials: 'same-origin' });
    if (!r.ok || !(r.headers.get('content-type') ?? '').includes('text/html')) return false;
    const served = entryScript(await r.text());
    return !!served && served !== current;
  } catch { return false; }
}

/**
 * **نسخةٌ جديدة والبرنامج مفتوح** (المراجعة المضادة لـ«التطبيق يتحدّث لوحده»، 24/09/2026): تطبيقُ
 * الموبايل يعود من الخلفية بالصفحة نفسها التي في الذاكرة — فلو لم يُغلَق أبدًا لبقي على نسخةٍ قديمة
 * مهما نُشر. فعند العودة بعد 5 دقائق غياب (وكل 30 دقيقة والصفحة ظاهرة) يُسأل الخادم، فإن كان
 * أحدث ظهر شريطٌ «نزلت نسخة جديدة — حدّث». **لا إعادة تحميل وحدها**: بياعٌ ترك استمارة حجزٍ
 * ليأخذ بيانات من واتساب يعود فيجدها كما تركها، ويضغط التحديث حين يفرغ.
 */
function watchForNewBuild(): void {
  let hiddenAt = 0;
  let shown = false;
  const check = (): void => {
    if (shown) return;
    void isNewBuildOut().then(yes => { if (yes && !shown) { shown = true; showUpdateBar(); } });
  };
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') { hiddenAt = Date.now(); return; }
    const away = hiddenAt ? Date.now() - hiddenAt : 0;
    hiddenAt = 0;
    if (away >= RECHECK_AFTER_HIDDEN_MS) check();
  });
  window.setInterval(() => { if (document.visibilityState === 'visible') check(); }, RECHECK_WHILE_VISIBLE_MS);
}

function showUpdateBar(): void {
  const bar = document.createElement('div');
  bar.className = 'update-bar';
  bar.setAttribute('role', 'status');
  const text = document.createElement('span');
  text.textContent = 'نزلت نسخة جديدة من البرنامج';
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.textContent = 'حدّث الآن';
  btn.addEventListener('click', () => window.location.reload());
  bar.append(text, btn);
  document.body.append(bar);
}
